import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Gauge, Droplet, Calculator, AlertTriangle, Trash2 } from 'lucide-react';
import IncidentsTab from '../components/IncidentsTab';

// Get current date in IST timezone
const getISTDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

function DailyOperations() {
  const { user } = useContext(AuthContext);
  const [pumpProfile, setPumpProfile] = useState(null);
  const [date, setDate] = useState(''); // No pre-fill - user must select date
  const [totalizerReadings, setTotalizerReadings] = useState([]);
  const [tankDipReadings, setTankDipReadings] = useState([]);
  const [ttReading, setTtReading] = useState({
    ms_quantity: '',
    ms_unit_price: '',
    hsd_quantity: '',
    hsd_unit_price: ''
  });
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [showZeroDipConfirm, setShowZeroDipConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ttToDelete, setTtToDelete] = useState(null);
  
  // State for yesterday's readings and sell prices
  const [yesterdayTotalizer, setYesterdayTotalizer] = useState([]);
  const [yesterdayDip, setYesterdayDip] = useState([]);
  const [todayStock, setTodayStock] = useState(null);
  const [sellPrices, setSellPrices] = useState({
    ms: '105.00',
    hsd: '92.00'
  });
  const [monthlyTTReadings, setMonthlyTTReadings] = useState([]);

  const isAdmin = user?.role === 'admin';

  // Validation helper - only allow valid numeric input
  const validateNumericInput = (value) => {
    // Allow empty string, digits, and one decimal point
    if (value === '' || value === '.') return true;
    const regex = /^\d*\.?\d*$/;
    return regex.test(value);
  };

  // Parse numeric value safely
  const parseNumericValue = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Format display value - show empty for 0, otherwise show value
  const formatDisplayValue = (value) => {
    if (value === 0 || value === '0' || value === '') return '';
    return value;
  };

  useEffect(() => {
    fetchPumpProfile();
    fetchMonthlyTTReadings();
  }, []);

  useEffect(() => {
    if (pumpProfile) {
      fetchReadingsForDate();
    }
  }, [date, pumpProfile]);

  const fetchPumpProfile = async () => {
    try {
      const res = await axios.get(`${API}/pump/profile`);
      setPumpProfile(res.data);
      
      const totReads = [];
      for (let i = 1; i <= res.data.ms_dispensers; i++) {
        totReads.push({ dispenser_number: i, fuel_type: 'MS', reading: '' });
      }
      for (let i = 1; i <= res.data.hsd_dispensers; i++) {
        totReads.push({ dispenser_number: i, fuel_type: 'HSD', reading: '' });
      }
      setTotalizerReadings(totReads);
      
      const tankReads = [];
      for (let i = 1; i <= res.data.ms_tanks; i++) {
        tankReads.push({ tank_number: i, fuel_type: 'MS', dip_reading: '' });
      }
      for (let i = 1; i <= res.data.hsd_tanks; i++) {
        tankReads.push({ tank_number: i, fuel_type: 'HSD', dip_reading: '' });
      }
      setTankDipReadings(tankReads);
    } catch (error) {
      toast.error('Failed to load pump profile');
    }
  };

  const fetchMonthlyTTReadings = async () => {
    try {
      // Get current month's start and end dates in IST (local) timezone
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      
      const startDate = `${year}-${month}-01`;
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
      
      const res = await axios.get(`${API}/tt-readings?start_date=${startDate}&end_date=${endDate}`);
      setMonthlyTTReadings(res.data.readings || []);
    } catch (error) {
      console.error('Failed to fetch monthly TT readings:', error);
      setMonthlyTTReadings([]);
    }
  };

  const fetchReadingsForDate = async () => {
    // Validate date before proceeding
    if (!date || date.length !== 10) {
      // Skip fetching for incomplete/invalid date strings
      return;
    }
    
    const selectedDate = new Date(date);
    if (isNaN(selectedDate.getTime())) {
      // Skip fetching for invalid dates
      console.log('Invalid date, skipping fetch:', date);
      return;
    }
    
    setDataLoading(true);
    try {
      // Get yesterday's date
      const yesterdayDate = new Date(selectedDate);
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterdayStr = `${yesterdayDate.getFullYear()}-${String(yesterdayDate.getMonth() + 1).padStart(2, '0')}-${String(yesterdayDate.getDate()).padStart(2, '0')}`;

      console.log(`Fetching data - Yesterday: ${yesterdayStr}, Today: ${date}`);

      // Fetch both requests in parallel
      const [yesterdayRes, todayRes] = await Promise.all([
        axios.get(`${API}/daily-stock?date=${yesterdayStr}`),
        axios.get(`${API}/daily-stock?date=${date}`)
      ]);

      // Process yesterday's readings (for opening values in table)
      if (yesterdayRes.data.stocks && yesterdayRes.data.stocks.length > 0) {
        const yesterdayData = yesterdayRes.data.stocks[0];
        console.log('Yesterday data found:', yesterdayData.date);
        setYesterdayTotalizer(yesterdayData.totalizer_readings || []);
        setYesterdayDip(yesterdayData.tank_dip_readings || []);
      } else {
        console.log('No yesterday data found');
        setYesterdayTotalizer([]);
        setYesterdayDip([]);
      }

      // Process today's saved readings (for closing values in table)
      if (todayRes.data.stocks && todayRes.data.stocks.length > 0) {
        const todayData = todayRes.data.stocks[0];
        console.log('Today data found:', todayData.date, 'Totalizer readings:', todayData.totalizer_readings?.length);
        setTodayStock(todayData);
      } else {
        console.log('No today data found for date:', date);
        setTodayStock(null);
      }
      
      // Reset input fields to empty (user will enter new readings)
      resetInputFields();
    } catch (error) {
      console.error('Failed to fetch readings:', error);
      // Reset states on error
      setYesterdayTotalizer([]);
      setYesterdayDip([]);
      setTodayStock(null);
    } finally {
      setDataLoading(false);
    }
  };

  const resetInputFields = () => {
    if (pumpProfile) {
      // Reset totalizer inputs
      const totReads = [];
      for (let i = 1; i <= pumpProfile.ms_dispensers; i++) {
        totReads.push({ dispenser_number: i, fuel_type: 'MS', reading: '' });
      }
      for (let i = 1; i <= pumpProfile.hsd_dispensers; i++) {
        totReads.push({ dispenser_number: i, fuel_type: 'HSD', reading: '' });
      }
      setTotalizerReadings(totReads);
      
      // Reset tank dip inputs
      const tankReads = [];
      for (let i = 1; i <= pumpProfile.ms_tanks; i++) {
        tankReads.push({ tank_number: i, fuel_type: 'MS', dip_reading: '' });
      }
      for (let i = 1; i <= pumpProfile.hsd_tanks; i++) {
        tankReads.push({ tank_number: i, fuel_type: 'HSD', dip_reading: '' });
      }
      setTankDipReadings(tankReads);
    }
  };

  const updateTotalizerReading = (index, value) => {
    // Validate input - only allow numbers and decimal point
    if (!validateNumericInput(value)) return;
    
    const updated = [...totalizerReadings];
    updated[index].reading = value;
    setTotalizerReadings(updated);
  };

  const updateTankDipReading = (index, value) => {
    // Validate input - only allow numbers and decimal point
    if (!validateNumericInput(value)) return;
    
    const updated = [...tankDipReadings];
    updated[index].dip_reading = value;
    setTankDipReadings(updated);
  };

  const updateTTReading = (field, value) => {
    // Validate input - only allow numbers and decimal point
    if (!validateNumericInput(value)) return;
    
    setTtReading({ ...ttReading, [field]: value });
  };

  const updateSellPrice = (fuelType, value) => {
    // Validate input - only allow numbers and decimal point
    if (!validateNumericInput(value)) return;
    
    setSellPrices({ ...sellPrices, [fuelType]: value });
  };

  // Handle focus - clear zero values
  const handleFocus = (e) => {
    if (e.target.value === '0' || e.target.value === '0.00') {
      e.target.value = '';
    }
  };

  // Save ONLY Totalizer Readings
  const handleTotalizerSubmit = async () => {
    // Validate date is selected
    if (!date) {
      toast.error('Please select a date first');
      return;
    }
    
    // Validate that all readings are valid numbers
    const invalidTotalizer = totalizerReadings.some(r => 
      r.reading !== '' && isNaN(parseFloat(r.reading))
    );
    
    if (invalidTotalizer) {
      toast.error('Please enter valid numeric values for totalizer readings');
      return;
    }

    // Check if at least one totalizer reading has a non-zero value
    const hasNonZeroTotalizer = totalizerReadings.some(r => 
      r.reading !== '' && parseFloat(r.reading) > 0
    );
    
    if (!hasNonZeroTotalizer) {
      toast.error('Please enter at least one non-zero totalizer reading before saving');
      return;
    }

    setLoading(true);
    try {
      // Convert empty strings to 0 for API
      const formattedTotalizer = totalizerReadings.map(r => ({
        ...r,
        reading: parseNumericValue(r.reading)
      }));

      // Get existing dip readings from todayStock or use empty array
      const existingDip = todayStock?.tank_dip_readings || [];

      await axios.post(`${API}/daily-stock`, {
        date: new Date(date).toISOString(),
        totalizer_readings: formattedTotalizer,
        tank_dip_readings: existingDip
      });
      toast.success('Totalizer readings saved successfully');
      
      // Reset totalizer input fields
      setTotalizerReadings(prev => prev.map(r => ({ ...r, reading: '' })));
      
      // Refresh data to show updated readings
      fetchReadingsForDate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save totalizer readings');
    } finally {
      setLoading(false);
    }
  };

  // Save ONLY Dip Readings - with confirmation for zeros
  const handleDipSubmit = async (confirmed = false) => {
    // Validate date is selected
    if (!date) {
      toast.error('Please select a date first');
      return;
    }
    
    // Validate that all readings are valid numbers
    const invalidDip = tankDipReadings.some(r => 
      r.dip_reading !== '' && isNaN(parseFloat(r.dip_reading))
    );
    
    if (invalidDip) {
      toast.error('Please enter valid numeric values for dip readings');
      return;
    }

    // Check if all dip readings are zero/empty
    const hasNonZeroDip = tankDipReadings.some(r => 
      r.dip_reading !== '' && parseFloat(r.dip_reading) > 0
    );
    
    // Show confirmation dialog if all zeros and not already confirmed
    if (!hasNonZeroDip && !confirmed) {
      setShowZeroDipConfirm(true);
      return;
    }

    setLoading(true);
    try {
      // Convert empty strings to 0 for API
      const formattedDip = tankDipReadings.map(r => ({
        ...r,
        dip_reading: parseNumericValue(r.dip_reading)
      }));

      // Get existing totalizer readings from todayStock or use empty array
      const existingTotalizer = todayStock?.totalizer_readings || [];

      await axios.post(`${API}/daily-stock`, {
        date: new Date(date).toISOString(),
        totalizer_readings: existingTotalizer,
        tank_dip_readings: formattedDip
      });
      toast.success('Dip readings saved successfully');
      
      // Reset dip input fields
      setTankDipReadings(prev => prev.map(r => ({ ...r, dip_reading: '' })));
      
      // Refresh data to show updated readings
      fetchReadingsForDate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save dip readings');
    } finally {
      setLoading(false);
      setShowZeroDipConfirm(false);
    }
  };

  const handleTTSubmit = async () => {
    // Validate date is selected
    if (!date) {
      toast.error('Please select a date first');
      return;
    }
    
    // Validate TT inputs
    const msQty = parseNumericValue(ttReading.ms_quantity);
    const msPrice = parseNumericValue(ttReading.ms_unit_price);
    const hsdQty = parseNumericValue(ttReading.hsd_quantity);
    const hsdPrice = parseNumericValue(ttReading.hsd_unit_price);

    if ((msQty > 0 && msPrice <= 0) || (hsdQty > 0 && hsdPrice <= 0)) {
      toast.error('Please enter unit price for quantities entered');
      return;
    }

    if (msQty === 0 && hsdQty === 0) {
      toast.error('Please enter at least one fuel quantity');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/tt-readings`, {
        date: new Date(date).toISOString(),
        ms_quantity: msQty,
        ms_unit_price: msPrice,
        hsd_quantity: hsdQty,
        hsd_unit_price: hsdPrice
      });
      toast.success(`TT reading recorded! New MS Stock: ${res.data.new_ms_stock}L, HSD Stock: ${res.data.new_hsd_stock}L`);
      
      // Reset TT input fields
      setTtReading({ ms_quantity: '', ms_unit_price: '', hsd_quantity: '', hsd_unit_price: '' });
      
      // Refresh TT readings for the month
      fetchMonthlyTTReadings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record TT reading');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTT = async () => {
    if (!ttToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/tt-readings/${ttToDelete.id}`);
      toast.success('TT reading deleted successfully');
      fetchMonthlyTTReadings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete TT reading');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setTtToDelete(null);
    }
  };

  const confirmDeleteTT = (tt) => {
    setTtToDelete(tt);
    setShowDeleteConfirm(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Helper to get yesterday's reading for a specific dispenser
  const getYesterdayTotalizerReading = (fuelType, dispenserNumber) => {
    const reading = yesterdayTotalizer.find(
      r => r.fuel_type === fuelType && r.dispenser_number === dispenserNumber
    );
    return parseNumericValue(reading?.reading);
  };

  // Helper to get today's SAVED reading for a specific dispenser (for table display)
  const getTodaySavedTotalizerReading = (fuelType, dispenserNumber) => {
    if (!todayStock || !todayStock.totalizer_readings) return 0;
    const reading = todayStock.totalizer_readings.find(
      r => r.fuel_type === fuelType && r.dispenser_number === dispenserNumber
    );
    return parseNumericValue(reading?.reading);
  };

  // Helper to get yesterday's dip reading for a specific tank
  const getYesterdayDipReading = (fuelType, tankNumber) => {
    const reading = yesterdayDip.find(
      r => r.fuel_type === fuelType && r.tank_number === tankNumber
    );
    return parseNumericValue(reading?.dip_reading);
  };

  // Helper to get today's SAVED dip reading for a specific tank (for table display)
  const getTodaySavedDipReading = (fuelType, tankNumber) => {
    if (!todayStock || !todayStock.tank_dip_readings) return 0;
    const reading = todayStock.tank_dip_readings.find(
      r => r.fuel_type === fuelType && r.tank_number === tankNumber
    );
    return parseNumericValue(reading?.dip_reading);
  };

  // Calculate totalizer sales summary using SAVED data
  const calculateTotalizerSales = () => {
    const msPrice = parseNumericValue(sellPrices.ms);
    const hsdPrice = parseNumericValue(sellPrices.hsd);
    
    // Get unique dispenser numbers from yesterday's data or today's saved data
    const msDispenserNums = new Set();
    const hsdDispenserNums = new Set();
    
    yesterdayTotalizer.forEach(r => {
      if (r.fuel_type === 'MS') msDispenserNums.add(r.dispenser_number);
      if (r.fuel_type === 'HSD') hsdDispenserNums.add(r.dispenser_number);
    });
    
    if (todayStock?.totalizer_readings) {
      todayStock.totalizer_readings.forEach(r => {
        if (r.fuel_type === 'MS') msDispenserNums.add(r.dispenser_number);
        if (r.fuel_type === 'HSD') hsdDispenserNums.add(r.dispenser_number);
      });
    }

    const msRows = Array.from(msDispenserNums).sort((a, b) => a - b).map(dispenserNum => {
      const opening = getYesterdayTotalizerReading('MS', dispenserNum);
      const closing = getTodaySavedTotalizerReading('MS', dispenserNum);
      const sale = closing - opening;
      const amount = sale * msPrice;
      return { dispenser: dispenserNum, fuelType: 'MS', opening, closing, sale, amount };
    });

    const hsdRows = Array.from(hsdDispenserNums).sort((a, b) => a - b).map(dispenserNum => {
      const opening = getYesterdayTotalizerReading('HSD', dispenserNum);
      const closing = getTodaySavedTotalizerReading('HSD', dispenserNum);
      const sale = closing - opening;
      const amount = sale * hsdPrice;
      return { dispenser: dispenserNum, fuelType: 'HSD', opening, closing, sale, amount };
    });

    const msTotalSale = msRows.reduce((sum, r) => sum + r.sale, 0);
    const msTotalAmount = msRows.reduce((sum, r) => sum + r.amount, 0);
    const hsdTotalSale = hsdRows.reduce((sum, r) => sum + r.sale, 0);
    const hsdTotalAmount = hsdRows.reduce((sum, r) => sum + r.amount, 0);

    return {
      msRows,
      hsdRows,
      msTotalSale,
      msTotalAmount,
      hsdTotalSale,
      hsdTotalAmount,
      grandTotal: msTotalAmount + hsdTotalAmount
    };
  };

  // Calculate dip stock summary using SAVED data
  const calculateDipSales = () => {
    const msPrice = parseNumericValue(sellPrices.ms);
    const hsdPrice = parseNumericValue(sellPrices.hsd);
    
    // Get unique tank numbers
    const msTankNums = new Set();
    const hsdTankNums = new Set();
    
    yesterdayDip.forEach(r => {
      if (r.fuel_type === 'MS') msTankNums.add(r.tank_number);
      if (r.fuel_type === 'HSD') hsdTankNums.add(r.tank_number);
    });
    
    if (todayStock?.tank_dip_readings) {
      todayStock.tank_dip_readings.forEach(r => {
        if (r.fuel_type === 'MS') msTankNums.add(r.tank_number);
        if (r.fuel_type === 'HSD') hsdTankNums.add(r.tank_number);
      });
    }

    const msRows = Array.from(msTankNums).sort((a, b) => a - b).map(tankNum => {
      const opening = getYesterdayDipReading('MS', tankNum);
      const closing = getTodaySavedDipReading('MS', tankNum);
      const sale = opening - closing; // For dip: opening stock - closing stock = consumed/sold
      const amount = sale * msPrice;
      return { tank: tankNum, fuelType: 'MS', opening, closing, sale, amount };
    });

    const hsdRows = Array.from(hsdTankNums).sort((a, b) => a - b).map(tankNum => {
      const opening = getYesterdayDipReading('HSD', tankNum);
      const closing = getTodaySavedDipReading('HSD', tankNum);
      const sale = opening - closing;
      const amount = sale * hsdPrice;
      return { tank: tankNum, fuelType: 'HSD', opening, closing, sale, amount };
    });

    const msTotalSale = msRows.reduce((sum, r) => sum + r.sale, 0);
    const msTotalAmount = msRows.reduce((sum, r) => sum + r.amount, 0);
    const hsdTotalSale = hsdRows.reduce((sum, r) => sum + r.sale, 0);
    const hsdTotalAmount = hsdRows.reduce((sum, r) => sum + r.amount, 0);

    return {
      msRows,
      hsdRows,
      msTotalSale,
      msTotalAmount,
      hsdTotalSale,
      hsdTotalAmount,
      grandTotal: msTotalAmount + hsdTotalAmount
    };
  };

  const totalizerSales = calculateTotalizerSales();
  const dipSales = calculateDipSales();

  if (!pumpProfile) {
    return <DashboardLayout><div className="text-center py-8">Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="daily-operations-page">
        <Card className="p-6 border border-border">
          <div className="flex flex-wrap gap-4 mb-6 items-end">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <Input
                data-testid="date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-48"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">MS Sell Price (₹/L)</label>
              <Input
                type="text"
                inputMode="decimal"
                value={sellPrices.ms}
                onChange={(e) => updateSellPrice('ms', e.target.value)}
                onFocus={handleFocus}
                placeholder="105.00"
                className="w-32"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">HSD Sell Price (₹/L)</label>
              <Input
                type="text"
                inputMode="decimal"
                value={sellPrices.hsd}
                onChange={(e) => updateSellPrice('hsd', e.target.value)}
                onFocus={handleFocus}
                placeholder="92.00"
                className="w-32"
              />
            </div>
          </div>

          <Tabs defaultValue="totalizer" className="w-full">
            <TabsList className="flex flex-wrap w-full gap-1 h-auto p-1">
              <TabsTrigger value="totalizer" data-testid="totalizer-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
                <Gauge className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
                Totalizer
              </TabsTrigger>
              <TabsTrigger value="tankdip" data-testid="tankdip-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
                <Droplet className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
                Tank Dip
              </TabsTrigger>
              <TabsTrigger value="ttreadings" data-testid="tt-readings-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
                <Droplet className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
                TT Readings
              </TabsTrigger>
              <TabsTrigger value="incidents" data-testid="incidents-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
                <AlertTriangle className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
                Incidents
              </TabsTrigger>
            </TabsList>

            <TabsContent value="totalizer" className="mt-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-petrol">MS (Petrol) Dispensers</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {totalizerReadings.filter(r => r.fuel_type === 'MS').map((reading, idx) => (
                      <div key={idx}>
                        <label className="block text-sm font-medium mb-2">
                          Dispenser {reading.dispenser_number}
                        </label>
                        <Input
                          data-testid={`ms-totalizer-${reading.dispenser_number}`}
                          type="text"
                          inputMode="decimal"
                          value={reading.reading}
                          onChange={(e) => updateTotalizerReading(
                            totalizerReadings.findIndex(r => r.fuel_type === 'MS' && r.dispenser_number === reading.dispenser_number),
                            e.target.value
                          )}
                          onFocus={handleFocus}
                          placeholder="Reading (L)"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-diesel">HSD (Diesel) Dispensers</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {totalizerReadings.filter(r => r.fuel_type === 'HSD').map((reading, idx) => (
                      <div key={idx}>
                        <label className="block text-sm font-medium mb-2">
                          Dispenser {reading.dispenser_number}
                        </label>
                        <Input
                          data-testid={`hsd-totalizer-${reading.dispenser_number}`}
                          type="text"
                          inputMode="decimal"
                          value={reading.reading}
                          onChange={(e) => updateTotalizerReading(
                            totalizerReadings.findIndex(r => r.fuel_type === 'HSD' && r.dispenser_number === reading.dispenser_number),
                            e.target.value
                          )}
                          onFocus={handleFocus}
                          placeholder="Reading (L)"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <Button 
                    data-testid="submit-totalizer-button"
                    onClick={handleTotalizerSubmit} 
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? 'Saving...' : 'Save Totalizer Readings'}
                  </Button>
                </div>

                {/* Totalizer Sales Summary Table */}
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Totalizer Sales Summary
                    {dataLoading && <span className="text-sm font-normal text-muted">(Loading...)</span>}
                  </h3>
                  
                  {/* Info message when data is missing */}
                  {!dataLoading && !todayStock && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
                      <strong>Note:</strong> No readings saved for {new Date(date).toLocaleDateString('en-IN')}. 
                      Enter and save readings above to see the sales summary.
                    </div>
                  )}
                  
                  {!dataLoading && yesterdayTotalizer.length === 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-sm">
                      <strong>Note:</strong> No opening readings found for {new Date(new Date(date).setDate(new Date(date).getDate() - 1)).toLocaleDateString('en-IN')} (previous day).
                    </div>
                  )}
                  
                  {/* MS Table */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-petrol mb-2">MS (Petrol) Sales</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-border text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-3 py-2 text-left border-b border-border">Dispenser</th>
                            <th className="px-3 py-2 text-right border-b border-border">Opening (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Closing (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Sale (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalizerSales.msRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-secondary/50">
                              <td className="px-3 py-2 border-b border-border">Dispenser {row.dispenser}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.opening.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.closing.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{row.sale.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-petrol/10 font-bold">
                            <td className="px-3 py-2 border-b border-border" colSpan={3}>MS Total</td>
                            <td className="px-3 py-2 text-right border-b border-border">{totalizerSales.msTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</td>
                            <td className="px-3 py-2 text-right border-b border-border">{formatCurrency(totalizerSales.msTotalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* HSD Table */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-diesel mb-2">HSD (Diesel) Sales</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-border text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-3 py-2 text-left border-b border-border">Dispenser</th>
                            <th className="px-3 py-2 text-right border-b border-border">Opening (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Closing (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Sale (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalizerSales.hsdRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-secondary/50">
                              <td className="px-3 py-2 border-b border-border">Dispenser {row.dispenser}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.opening.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.closing.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{row.sale.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-diesel/10 font-bold">
                            <td className="px-3 py-2 border-b border-border" colSpan={3}>HSD Total</td>
                            <td className="px-3 py-2 text-right border-b border-border">{totalizerSales.hsdTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</td>
                            <td className="px-3 py-2 text-right border-b border-border">{formatCurrency(totalizerSales.hsdTotalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">Grand Total (Totalizer)</span>
                      <span className="text-2xl font-bold text-primary">{formatCurrency(totalizerSales.grandTotal)}</span>
                    </div>
                    <div className="flex gap-6 mt-2 text-sm text-muted">
                      <span>MS: {totalizerSales.msTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</span>
                      <span>HSD: {totalizerSales.hsdTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="tankdip" className="mt-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-petrol">MS (Petrol) Tanks</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {tankDipReadings.filter(r => r.fuel_type === 'MS').map((reading, idx) => (
                      <div key={idx}>
                        <label className="block text-sm font-medium mb-2">
                          Tank {reading.tank_number}
                        </label>
                        <Input
                          data-testid={`ms-tank-${reading.tank_number}`}
                          type="text"
                          inputMode="decimal"
                          value={reading.dip_reading}
                          onChange={(e) => updateTankDipReading(
                            tankDipReadings.findIndex(r => r.fuel_type === 'MS' && r.tank_number === reading.tank_number),
                            e.target.value
                          )}
                          onFocus={handleFocus}
                          placeholder="Dip Reading (L)"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-diesel">HSD (Diesel) Tanks</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {tankDipReadings.filter(r => r.fuel_type === 'HSD').map((reading, idx) => (
                      <div key={idx}>
                        <label className="block text-sm font-medium mb-2">
                          Tank {reading.tank_number}
                        </label>
                        <Input
                          data-testid={`hsd-tank-${reading.tank_number}`}
                          type="text"
                          inputMode="decimal"
                          value={reading.dip_reading}
                          onChange={(e) => updateTankDipReading(
                            tankDipReadings.findIndex(r => r.fuel_type === 'HSD' && r.tank_number === reading.tank_number),
                            e.target.value
                          )}
                          onFocus={handleFocus}
                          placeholder="Dip Reading (L)"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <Button 
                    data-testid="submit-readings-button"
                    onClick={() => handleDipSubmit(false)} 
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? 'Saving...' : 'Save Tank Dip Readings'}
                  </Button>
                </div>

                {/* Dip Sales Summary Table */}
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Tank Dip Sales Summary
                    {dataLoading && <span className="text-sm font-normal text-muted">(Loading...)</span>}
                  </h3>
                  <p className="text-sm text-muted mb-4">
                    Note: Sale calculated as Opening Stock - Closing Stock (does not account for TT deliveries)
                  </p>
                  
                  {/* Info message when data is missing */}
                  {!dataLoading && !todayStock && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
                      <strong>Note:</strong> No readings saved for {new Date(date).toLocaleDateString('en-IN')}. 
                      Enter and save readings above to see the sales summary.
                    </div>
                  )}
                  
                  {/* MS Table */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-petrol mb-2">MS (Petrol) Stock</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-border text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-3 py-2 text-left border-b border-border">Tank</th>
                            <th className="px-3 py-2 text-right border-b border-border">Opening (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Closing (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Consumed (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dipSales.msRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-secondary/50">
                              <td className="px-3 py-2 border-b border-border">Tank {row.tank}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.opening.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.closing.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className={`px-3 py-2 text-right border-b border-border font-medium ${row.sale < 0 ? 'text-green-600' : ''}`}>
                                {row.sale.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-petrol/10 font-bold">
                            <td className="px-3 py-2 border-b border-border" colSpan={3}>MS Total</td>
                            <td className="px-3 py-2 text-right border-b border-border">{dipSales.msTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</td>
                            <td className="px-3 py-2 text-right border-b border-border">{formatCurrency(dipSales.msTotalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* HSD Table */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-diesel mb-2">HSD (Diesel) Stock</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-border text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-3 py-2 text-left border-b border-border">Tank</th>
                            <th className="px-3 py-2 text-right border-b border-border">Opening (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Closing (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Consumed (L)</th>
                            <th className="px-3 py-2 text-right border-b border-border">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dipSales.hsdRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-secondary/50">
                              <td className="px-3 py-2 border-b border-border">Tank {row.tank}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.opening.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right border-b border-border">{row.closing.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className={`px-3 py-2 text-right border-b border-border font-medium ${row.sale < 0 ? 'text-green-600' : ''}`}>
                                {row.sale.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-right border-b border-border font-medium">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-diesel/10 font-bold">
                            <td className="px-3 py-2 border-b border-border" colSpan={3}>HSD Total</td>
                            <td className="px-3 py-2 text-right border-b border-border">{dipSales.hsdTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</td>
                            <td className="px-3 py-2 text-right border-b border-border">{formatCurrency(dipSales.hsdTotalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">Grand Total (Dip)</span>
                      <span className="text-2xl font-bold text-primary">{formatCurrency(dipSales.grandTotal)}</span>
                    </div>
                    <div className="flex gap-6 mt-2 text-sm text-muted">
                      <span>MS: {dipSales.msTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</span>
                      <span>HSD: {dipSales.hsdTotalSale.toLocaleString('en-IN', { minimumFractionDigits: 2 })} L</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ttreadings" className="mt-6">
              <div className="space-y-6">
                <div className="bg-accent/5 border border-accent/20 rounded-md p-4 mb-6">
                  <p className="text-sm font-medium">
                    <strong>TT Readings:</strong> Record fuel deliveries from Tank Trucks. This will automatically add the delivered fuel to your current stock levels.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-petrol">MS (Petrol) Delivery</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Quantity Delivered (L) *
                      </label>
                      <Input
                        data-testid="tt-ms-quantity"
                        type="text"
                        inputMode="decimal"
                        value={ttReading.ms_quantity}
                        onChange={(e) => updateTTReading('ms_quantity', e.target.value)}
                        onFocus={handleFocus}
                        placeholder="e.g., 10000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Unit Price (₹/L) *
                      </label>
                      <Input
                        data-testid="tt-ms-price"
                        type="text"
                        inputMode="decimal"
                        value={ttReading.ms_unit_price}
                        onChange={(e) => updateTTReading('ms_unit_price', e.target.value)}
                        onFocus={handleFocus}
                        placeholder="e.g., 95.50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Total Amount (₹)
                      </label>
                      <Input
                        type="text"
                        value={formatCurrency(parseNumericValue(ttReading.ms_quantity) * parseNumericValue(ttReading.ms_unit_price))}
                        readOnly
                        className="bg-secondary"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-diesel">HSD (Diesel) Delivery</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Quantity Delivered (L) *
                      </label>
                      <Input
                        data-testid="tt-hsd-quantity"
                        type="text"
                        inputMode="decimal"
                        value={ttReading.hsd_quantity}
                        onChange={(e) => updateTTReading('hsd_quantity', e.target.value)}
                        onFocus={handleFocus}
                        placeholder="e.g., 15000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Unit Price (₹/L) *
                      </label>
                      <Input
                        data-testid="tt-hsd-price"
                        type="text"
                        inputMode="decimal"
                        value={ttReading.hsd_unit_price}
                        onChange={(e) => updateTTReading('hsd_unit_price', e.target.value)}
                        onFocus={handleFocus}
                        placeholder="e.g., 88.50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Total Amount (₹)
                      </label>
                      <Input
                        type="text"
                        value={formatCurrency(parseNumericValue(ttReading.hsd_quantity) * parseNumericValue(ttReading.hsd_unit_price))}
                        readOnly
                        className="bg-secondary"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-md">
                    <div>
                      <p className="text-sm font-medium">Total Delivery Value</p>
                      <p className="text-2xl font-bold mt-1">
                        {formatCurrency(
                          (parseNumericValue(ttReading.ms_quantity) * parseNumericValue(ttReading.ms_unit_price)) + 
                          (parseNumericValue(ttReading.hsd_quantity) * parseNumericValue(ttReading.hsd_unit_price))
                        )}
                      </p>
                    </div>
                    <Button 
                      data-testid="submit-tt-reading-button"
                      onClick={handleTTSubmit} 
                      disabled={loading}
                      size="lg"
                    >
                      {loading ? 'Saving...' : 'Record TT Delivery'}
                    </Button>
                  </div>
                </div>

                {/* Monthly TT Readings Table */}
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    TT Deliveries - {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                  </h3>
                  
                  {monthlyTTReadings.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full border border-border text-sm">
                          <thead className="bg-secondary">
                            <tr>
                              <th className="px-3 py-2 text-left border-b border-border">Date</th>
                              <th className="px-3 py-2 text-right border-b border-border">MS Qty (L)</th>
                              <th className="px-3 py-2 text-right border-b border-border">MS Rate (₹)</th>
                              <th className="px-3 py-2 text-right border-b border-border">MS Amount (₹)</th>
                              <th className="px-3 py-2 text-right border-b border-border">HSD Qty (L)</th>
                              <th className="px-3 py-2 text-right border-b border-border">HSD Rate (₹)</th>
                              <th className="px-3 py-2 text-right border-b border-border">HSD Amount (₹)</th>
                              <th className="px-3 py-2 text-right border-b border-border">Total (₹)</th>
                              {isAdmin && <th className="px-3 py-2 text-center border-b border-border">Action</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {monthlyTTReadings.map((tt, idx) => (
                              <tr key={tt.id || idx} className="hover:bg-secondary/50">
                                <td className="px-3 py-2 border-b border-border">
                                  {new Date(tt.date + 'Z').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border">
                                  {tt.ms_quantity?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border">
                                  {tt.ms_unit_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border font-medium text-petrol">
                                  {formatCurrency(tt.ms_total_amount || 0)}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border">
                                  {tt.hsd_quantity?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border">
                                  {tt.hsd_unit_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border font-medium text-diesel">
                                  {formatCurrency(tt.hsd_total_amount || 0)}
                                </td>
                                <td className="px-3 py-2 text-right border-b border-border font-bold">
                                  {formatCurrency((tt.ms_total_amount || 0) + (tt.hsd_total_amount || 0))}
                                </td>
                                {isAdmin && (
                                  <td className="px-3 py-2 text-center border-b border-border">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => confirmDeleteTT(tt)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-tt-${tt.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-primary/10 font-bold">
                            <tr>
                              <td className="px-3 py-2 border-t-2 border-primary">TOTAL</td>
                              <td className="px-3 py-2 text-right border-t-2 border-primary">
                                {monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_quantity || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} L
                              </td>
                              <td className="px-3 py-2 border-t-2 border-primary"></td>
                              <td className="px-3 py-2 text-right border-t-2 border-primary text-petrol">
                                {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_total_amount || 0), 0))}
                              </td>
                              <td className="px-3 py-2 text-right border-t-2 border-primary">
                                {monthlyTTReadings.reduce((sum, tt) => sum + (tt.hsd_quantity || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} L
                              </td>
                              <td className="px-3 py-2 border-t-2 border-primary"></td>
                              <td className="px-3 py-2 text-right border-t-2 border-primary text-diesel">
                                {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.hsd_total_amount || 0), 0))}
                              </td>
                              <td className="px-3 py-2 text-right border-t-2 border-primary text-primary">
                                {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_total_amount || 0) + (tt.hsd_total_amount || 0), 0))}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Monthly Summary Cards */}
                      <div className="grid md:grid-cols-3 gap-4 mt-4">
                        <div className="p-4 bg-petrol/10 border border-petrol/30 rounded-lg">
                          <p className="text-sm text-muted">Total MS Received</p>
                          <p className="text-xl font-bold text-petrol">
                            {monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_quantity || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} L
                          </p>
                          <p className="text-sm font-medium mt-1">
                            {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_total_amount || 0), 0))}
                          </p>
                        </div>
                        <div className="p-4 bg-diesel/10 border border-diesel/30 rounded-lg">
                          <p className="text-sm text-muted">Total HSD Received</p>
                          <p className="text-xl font-bold text-diesel">
                            {monthlyTTReadings.reduce((sum, tt) => sum + (tt.hsd_quantity || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} L
                          </p>
                          <p className="text-sm font-medium mt-1">
                            {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.hsd_total_amount || 0), 0))}
                          </p>
                        </div>
                        <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                          <p className="text-sm text-muted">Total TT Value</p>
                          <p className="text-xl font-bold text-primary">
                            {formatCurrency(monthlyTTReadings.reduce((sum, tt) => sum + (tt.ms_total_amount || 0) + (tt.hsd_total_amount || 0), 0))}
                          </p>
                          <p className="text-sm font-medium mt-1">
                            {monthlyTTReadings.length} Deliveries
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted">
                      <p>No TT deliveries recorded this month.</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Incidents Tab */}
            <TabsContent value="incidents" className="mt-6">
              <IncidentsTab />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Zero Dip Readings Confirmation Dialog */}
      <Dialog open={showZeroDipConfirm} onOpenChange={setShowZeroDipConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Zero Dip Readings
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted">
              All dip readings are empty or zero. Are you sure you want to save these readings?
            </p>
            <p className="text-sm text-muted mt-2">
              This will record zero stock levels for {new Date(date).toLocaleDateString('en-IN')}.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowZeroDipConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleDipSubmit(true)}>
              Yes, Save Zero Readings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete TT Reading Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete TT Reading
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted">
              Are you sure you want to delete this TT reading?
            </p>
            {ttToDelete && (
              <div className="mt-3 p-3 bg-secondary rounded-md text-sm">
                <p><strong>Date:</strong> {new Date(ttToDelete.date + 'Z').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })}</p>
                <p><strong>MS:</strong> {ttToDelete.ms_quantity?.toLocaleString('en-IN')}L @ ₹{ttToDelete.ms_unit_price}</p>
                <p><strong>HSD:</strong> {ttToDelete.hsd_quantity?.toLocaleString('en-IN')}L @ ₹{ttToDelete.hsd_unit_price}</p>
                <p className="font-bold mt-2">Total: {formatCurrency((ttToDelete.ms_total_amount || 0) + (ttToDelete.hsd_total_amount || 0))}</p>
              </div>
            )}
            <p className="text-sm text-red-500 mt-3">
              This action cannot be undone. Related station ledger entries will also be deleted.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTT} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default DailyOperations;
